import { useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Chip, Stack,
  Tooltip, Dialog, DialogContent, DialogActions,
  Button, TextField, MenuItem, IconButton, Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import type { KanbanBoard, KanbanCard, TaskState } from '@pm/shared';
import { KANBAN_STATES, normalizeKanbanState, useCreateKanbanCard, useUpdateKanbanCard, useDeleteKanbanCard } from '../../../../api/hooks/useKanban';
import { PmRichTextEditor, isRichTextEmpty } from '../../../../components/pm/RichTextEditor';
import { useNotifications } from '../../../../context/NotificationContext';
import { useCreateProjektCard, useUpdateProjektCard, useDeleteProjektCard, projektKanbanFehler } from '../../../../api/hooks/useProjektKanban';
import { KanbanCardItem } from './KanbanCardItem';
import { apiErrorMessage } from '../../../../lib/apiErrorMessage';
import { AppDialogTitle } from '../../../../components/common/AppDialogTitle';

const COLUMN_COLORS: Record<string, string> = {
  PLANNED: '#90a4ae', IN_PROGRESS: '#1D8296', COMPLETED: '#66bb6a',
};

interface Props {
  projectId: string;
  /** null = Projekt-Board ohne Arbeitspaket (PHQ-104). */
  apId: string | null;
  board: KanbanBoard;
  /** true = aktives Board (beschreibbar). false = eingefroren (read-only). */
  active: boolean;
  /** Ausgeblendete Karten abgeschwächt mit anzeigen. */
  showHidden: boolean;
  /** Personen-Picker-Optionen (P-14): RACI-Personen aller APs des Projekts. */
  personOptions?: string[];
}

interface CardDraft {
  id?: string;
  title: string;
  description: string;
  person: string;
  state: TaskState;
}

export function KanbanBoardView({ projectId, apId, board, active, showHidden, personOptions = [] }: Props) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();
  // PHQ-104: Projekt-Boards (apId = null) nutzen die Projekt-Hooks, gleiche Variablenform.
  const istProjektBoard = apId === null;
  const apCreate = useCreateKanbanCard(projectId);
  const apUpdate = useUpdateKanbanCard(projectId);
  const apDelete = useDeleteKanbanCard(projectId);
  const projCreate = useCreateProjektCard(projectId);
  const projUpdate = useUpdateProjektCard(projectId);
  const projDelete = useDeleteProjektCard(projectId);
  const createCard = (istProjektBoard ? projCreate : apCreate) as typeof apCreate;
  const updateCard = (istProjektBoard ? projUpdate : apUpdate) as typeof apUpdate;
  const deleteCard = (istProjektBoard ? projDelete : apDelete) as typeof apDelete;
  const fehler = (err: unknown) => (istProjektBoard
    ? projektKanbanFehler(err, tc('errors.generic', 'Fehler'))
    : apiErrorMessage(err, tc('errors.generic', 'Fehler')));

  const [dragOverCol, setDragOverCol] = useState<string | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<CardDraft | null>(null);

  const handleDrop = useCallback(async (e: React.DragEvent, targetState: TaskState) => {
    e.preventDefault();
    setDragOverCol(null);
    setDraggedId(null);
    const cardId = e.dataTransfer.getData('text/plain');
    if (!cardId) return;
    const card = board.cards.find((c) => c.id === cardId);
    if (!card || card.state === targetState) return;
    try {
      await updateCard.mutateAsync({ apId: apId as string, cardId, data: { state: targetState } });
    } catch (err) {
      notify(fehler(err), 'error');
    }
  }, [board.cards, apId, updateCard, notify, fehler]);

  const toggleHidden = async (card: KanbanCard) => {
    try {
      await updateCard.mutateAsync({ apId: apId as string, cardId: card.id, data: { hidden: !card.hidden } });
    } catch (err) { notify(fehler(err), 'error'); }
  };

  const removeCard = async (card: KanbanCard) => {
    try {
      await deleteCard.mutateAsync({ apId: apId as string, cardId: card.id });
    } catch (err) { notify(fehler(err), 'error'); }
  };

  // P-17: Karte abschließen/wiedereröffnen — reiner State-Wechsel, kein Löschen/Umhängen.
  const completeCard = async (card: KanbanCard) => {
    try {
      await updateCard.mutateAsync({ apId: apId as string, cardId: card.id, data: { state: 'COMPLETED' } });
    } catch (err) { notify(fehler(err), 'error'); }
  };

  const reopenCard = async (card: KanbanCard) => {
    try {
      await updateCard.mutateAsync({ apId: apId as string, cardId: card.id, data: { state: 'IN_PROGRESS' } });
    } catch (err) { notify(fehler(err), 'error'); }
  };

  const saveDraft = async () => {
    if (!draft) return;
    const data = {
      title: draft.title.trim(),
      description: isRichTextEmpty(draft.description) ? null : draft.description,
      person: draft.person.trim() || null,
      state: draft.state,
    };
    try {
      if (draft.id) {
        await updateCard.mutateAsync({ apId: apId as string, cardId: draft.id, data });
      } else {
        await createCard.mutateAsync({ apId: apId as string, boardId: board.id, data });
      }
      setDraft(null);
    } catch (err) { notify(fehler(err), 'error'); }
  };

  const grouped = KANBAN_STATES.reduce<Record<string, KanbanCard[]>>((acc, col) => {
    acc[col] = board.cards.filter((c) => normalizeKanbanState(c.state) === col && (showHidden || !c.hidden));
    return acc;
  }, {});

  return (
    <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
      {KANBAN_STATES.map((col) => {
        const isDropTarget = active && dragOverCol === col;
        return (
          <Paper
            key={col}
            variant="outlined"
            sx={{
              minWidth: 220, maxWidth: 300, flex: 1, display: 'flex', flexDirection: 'column',
              bgcolor: 'background.default',
              ...(isDropTarget ? { boxShadow: `inset 0 0 0 2px ${COLUMN_COLORS[col]}`, bgcolor: 'action.hover' } : {}),
            }}
            onDragOver={active ? (e) => { e.preventDefault(); setDragOverCol(col); } : undefined}
            onDragLeave={active ? () => setDragOverCol(null) : undefined}
            onDrop={active ? (e) => handleDrop(e, col) : undefined}
          >
            <Box sx={{ px: 1.5, py: 0.75, borderBottom: '3px solid', borderColor: COLUMN_COLORS[col] ?? 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" fontWeight={700}>{t(`states.${col}`, col)}</Typography>
                <Chip label={grouped[col].length} size="small" variant="outlined" />
              </Stack>
              {active && (
                <Tooltip title={t('kanban.addCard', 'Karte hinzufügen')}>
                  <IconButton size="small" sx={{ color: COLUMN_COLORS[col] }} onClick={() => setDraft({ title: '', description: '', person: '', state: col })}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ p: 1, flex: 1, overflowY: 'auto', minHeight: 60 }}>
              <Stack spacing={1}>
                {grouped[col].map((card) => (
                  <KanbanCardItem
                    key={card.id}
                    card={card}
                    active={active}
                    dragging={draggedId === card.id}
                    onDragStart={(e) => { e.dataTransfer.setData('text/plain', card.id); setDraggedId(card.id); }}
                    onDragEnd={() => setDraggedId(null)}
                    onEdit={() => setDraft({ id: card.id, title: card.title, description: card.description ?? '', person: card.person ?? '', state: card.state })}
                    onToggleHidden={() => toggleHidden(card)}
                    onRemove={() => removeCard(card)}
                    onComplete={() => completeCard(card)}
                    onReopen={() => reopenCard(card)}
                  />
                ))}
                {grouped[col].length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 2, display: 'block' }}>
                    {t('kanban.emptyColumn', '—')}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Paper>
        );
      })}

      <Dialog open={!!draft} onClose={() => setDraft(null)} maxWidth="sm" fullWidth>
        <AppDialogTitle>{draft?.id ? t('kanban.editCard', 'Karte bearbeiten') : t('kanban.addCard', 'Karte hinzufügen')}</AppDialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('kanban.cardTitle', 'Titel')} value={draft?.title ?? ''} fullWidth autoFocus
              onChange={(e) => setDraft((d) => d && { ...d, title: e.target.value })}
            />
            <Box>
              <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)', mb: 0.5, display: 'block' }}>
                {t('kanban.cardDescription', 'Beschreibung')}
              </Typography>
              <PmRichTextEditor
                value={draft?.description ?? ''}
                onChange={(html) => setDraft((d) => d && { ...d, description: html })}
                placeholder={t('kanban.cardDescriptionPlaceholder', 'Beschreibung der Aufgabe …')}
                minHeight={120}
              />
            </Box>
            <Autocomplete
              freeSolo
              options={personOptions}
              value={draft?.person ?? ''}
              onChange={(_e, newValue) => setDraft((d) => d && { ...d, person: newValue ?? '' })}
              onInputChange={(_e, newInputValue) => setDraft((d) => d && { ...d, person: newInputValue })}
              renderInput={(params) => (
                <TextField {...params} label={t('kanban.cardPerson', 'Person')} fullWidth />
              )}
            />
            <TextField
              select label={t('kanban.cardState', 'Status')} value={draft?.state ?? 'PLANNED'} fullWidth
              onChange={(e) => setDraft((d) => d && { ...d, state: e.target.value as TaskState })}
            >
              {KANBAN_STATES.map((s) => (
                <MenuItem key={s} value={s}>{t(`states.${s}`, s)}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraft(null)}>{tc('actions.cancel', 'Abbrechen')}</Button>
          <Button variant="contained" disabled={!draft?.title.trim()} onClick={saveDraft}>{tc('actions.save', 'Speichern')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
